import { useState, useRef } from "react";
import { Box, Typography, Button, Paper, CircularProgress, Alert, Snackbar } from "@mui/material";
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import DashboardLayout from "@/components/DashboardLayout";
import { uploadCsvImport, DatenimportResult } from "@/api/datenimport";

const DataImport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DatenimportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const importResult = await uploadCsvImport(file);
      setResult(importResult);
    } catch (err: any) {
      setError(err?.message || "Fehler beim Datenimport.");
    } finally {
      setLoading(false);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 800, mx: "auto", mt: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Datenimport
        </Typography>

        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Hier können Sie Daten aus dem alten Portal importieren. Laden Sie
            dazu eine CSV-Datei hoch, die die Patientendaten im erwarteten
            Format enthält. Diese Importfunktion dient der Migration aus dem
            alten System und wird zu gegebener Zeit abgestellt.
          </Typography>

          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleImport}
          />

          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
          >
            {loading ? "Import läuft…" : "CSV-Datei importieren"}
          </Button>
        </Paper>

        {result && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Import-Ergebnis
            </Typography>
            <Typography variant="body2">Verarbeitete Zeilen: {result.totalRows}</Typography>
            <Typography variant="body2">Kunden neu angelegt: {result.kundenNeuAngelegt}</Typography>
            <Typography variant="body2">Patientinnen neu angelegt: {result.patientenNeuAngelegt}</Typography>
            <Typography variant="body2">Patientinnen aktualisiert: {result.patientenAktualisiert}</Typography>
            <Typography variant="body2">Materialien neu angelegt: {result.materialienNeuAngelegt}</Typography>
            <Typography variant="body2">Hersteller neu angelegt: {result.herstellerNeuAngelegt}</Typography>
            {result.fehler && result.fehler.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error">
                  Fehler:
                </Typography>
                {result.fehler.map((f, i) => (
                  <Typography key={i} variant="body2" color="error">
                    {f}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        )}
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default DataImport;
